import { NetworkData } from '../../locality/networkDataStorage';
import { NodeState, PeeringProtocol } from './data';

export enum SourceType {
  INGEST = 'INGEST',
  GRID = 'GRID',
  PEER = 'PEER',
  PULL_THROUGH = 'PULL_THROUGH',
  CACHE_HIT = 'CACHE_HIT',
  GRAPH = 'GRAPH',
  PROXY = 'PROXY',
  PROXY_CACHE = 'PROXY_CACHE'
}

export enum ConnectionState {
  // New connection
  PENDING = 'PENDING',
  // Successful connection
  ESTABLISHED = 'ESTABLISHED',
  // disconnected but retrying connection
  DISCONNECTED = 'DISCONNECTED',
  // Terminal
  FAILED = 'FAILED',
  CLOSED = 'CLOSED'
}

export interface Component {
  id: string;
  pos: number;
  sizeBytes: number;
}

export interface TransferRecord {
  type: SourceType;
  sourceId: string;
  nodeId: string;
  contentId: string;
  objectId: string;
  component: Component;
  timestamp: number;
  requestStart: number;
  loadInit: number;
  requestEnd: number;
  status: boolean;
  statusCode: number;
  tenantId: string;
  clusterId: string | undefined;
  externalIp: string | undefined;
  internalIp: string | undefined;
  internalMdns: string | undefined;
  appName: string;
  playType: string | undefined;
  peerEnabled: boolean;
}

export interface NetworkTuple {
  nodeId: string;
  extIp: string | undefined;
  intIp: string | undefined;
  port: number | undefined;
  depth: number;
  capacity: number;
  intMdns: string | undefined;
}

export interface PeerConnection {
  type: string;
  state: string;
  source: NetworkTuple;
  dest: NetworkTuple;
  protocol: PeeringProtocol;
  statusCode: number;
}

export interface ConnectionRecord {
  nodeId: string;
  agentId: string;
  contentId: string;
  tenantId: string;
  clusterId: string;
  locality: string;
  connection: PeerConnection;
  timestamp: number;
  monotonicTime: number;
}

export interface ContentNotificationRecord {
  nodeId: string;
  agentId: string;
  contentId: string;
  tenantId: string;
  clusterId: string;
  locality: string;
  timestamp: number;
  monotonicTime: number;
  peerId: string;
  objectId: string;
  status: string;
}

export interface NodeStateRecord {
  nodeId: string;
  agentId: string;
  contentId: string;
  tenantId: string;
  clusterId: string;
  locality: string;
  timestamp: number;
  monotonicTime: number;
  reporterId: string | undefined;
  depth: number | undefined;
  state: NodeState;
}

export interface PeerNotificationRecord {
  nodeId: string;
  agentId: string;
  contentId: string;
  tenantId: string;
  clusterId: string;
  locality: string;
  timestamp: number;
  monotonicTime: number;
  peerId: string;
  reportedPeer: string;
  state: NodeState;
}

export interface ClusterDetachRecord {
  nodeId: string;
  agentId: string;
  contentId: string;
  tenantId: string;
  clusterId: string;
  locality: string;
  timestamp: number;
  monotonicTime: number;
  reportingPeer: string;
  detachingPeer: string;
}

export interface PeerRequestRecord {
  nodeId: string;
  agentId: string;
  contentId: string;
  tenantId: string;
  clusterId: string;
  locality: string;
  peerId: string;
  originId: string;
  timestamp: number;
  requestTime: number;
  responeTime: number;
  candidates: string[] | undefined;
  path: string[] | undefined;
  statusCode: number;
}

export interface ClusterCandidateRecord {
  clusterId: string;
  candidates: string[];
}

export interface AttachmentRequestRecord {
  nodeId: string;
  agentId: string;
  contentId: string;
  tenantId: string;
  clusterId: string | undefined;
  locality: string;
  timestamp: number;
  requestTime: number;
  responseTime: number;
  clusterCandidates: ClusterCandidateRecord[] | undefined;
  backupCluster: string | undefined;
  statusCode: number;
}

export interface Item {
  id: string;
  url: string;
}

export interface PeerData {
  payload: Uint8Array;
  mime: string;
  headers?: Map<string, string>;
}

export interface Node extends NetworkData {
  tenantId: string;
  contentId: string;
  nodeId: string;
  agentId: string;
  clusterId?: string;
}

export interface PeerResponse {
  node: Node;
  data: PeerData;
  ts: number;
}

export interface RequestError {
  error: { message: string; statusCode: number };
  node: Node;
  unDeliverable?: boolean;
}

export interface Content {
  id: string;
  tenantId: string;
  items: Item[];
}

export default interface Agent {
  node: Node;

  tenantId: string;

  content: Content;

  initialized: boolean;

  isShutdown: boolean;

  setupDelay?: number;

  /**
   * Given a url, return an objectId.
   */
  asObjectId(url: string): string;

  /**
   * Initialize the Agent. initialized should be true when this completes.
   */
  init(): Promise<void>;

  /**
   * Shutdown the Agent. Clean up all resources and set initialized to false.
   */
  shutdown(): void;

  /**
   * Returns true if the url is a manifest url
   */
  isManifestUrl(url: string): boolean;

  /**
   * Fetch an object from the peer cluster. the promise returns a peer response or errors with a RequestError
   */
  fetchObject(url: string, timeout: number): Promise<PeerResponse>;

  /**
   * When an object is pulled from cdn or origin, we put in the in the peer manager to be cached.
   */
  putObject(url: string, data: PeerData): void;

  /**
   * Notify the cluster that the url is being sourced
   */
  notifyPending(url: string): void;

  /**
   * Notify the cluster that the url is being evicted
   */
  notifyEvicted(url: string): void;

  /**
   * Notify the cluster that the url is being evicted
   */
  getNode(): Node;

  /**
   * Send a TransferRecord.
   */
  sendTransferRecord(record: TransferRecord): void;
}
